import { Component, Vue } from 'vue-property-decorator'
import UserConfigurationPrintTabModel
    from '@/components/userConfiguration/print/tab-model/user-configuration-print-tab-model.vue'
import UserConfigurationPrintTabGeneral
    from '@/components/userConfiguration/print/tab-general/user-configuration-print-tab-general.vue'
import {
    initialUserConfigurationPrintTabState,
    UserConfigurationPrintTabState
} from '@/components/userConfiguration/print/tab/user-configuration-print-tab.model'
import sessionStore from '@/session/sessionStore'
import { anapathPrinterRestClientService } from '@/modules/printModule/services/anapath-printer-rest-client/anapath-printer-rest-client.service'
import {
    ReportModelConfigurationMatch,
    UserappReportModelConfigurationRequest
} from '@/modules/printModule/services/report-model-configuration/report-model-configuration.model'
import { reportModelConfigurationService } from '@/modules/printModule/services/report-model-configuration/report-model-configuration.service'
import { orderBy } from 'lodash'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto'

@Component({
    components: {
        UserConfigurationPrintTabGeneral,
        UserConfigurationPrintTabModel
    }
})
export default class UserConfigurationPrintTab extends Vue {
    state: UserConfigurationPrintTabState
    constructor() {
        super()
        this.state = initialUserConfigurationPrintTabState()
    }

    get printServiceStatus(): boolean {
        return this.state.status
    }

    get isPersonnel(): boolean {
        return this.$store.getters.userInformation?.userType === UserType.PERSONNEL
    }

    get defaultReports(): ReportModelConfigurationMatch[] {
        return this.state.reportModels.filter(reportModel => reportModel.isDefault)
    }

    get modelReports(): ReportModelConfigurationMatch[] {
        return this.state.reportModels.filter(reportModel => !reportModel.isDefault)
    }

    async mounted(): Promise<void> {
        await sessionStore.checkPrintServiceStatus(false)
        this.state.status = this.$store.getters.printServiceStatus

        if (this.printServiceStatus) {
            try {
                this.$_updateLoading(true)
                await Promise.all([
                    this._loadWorkstation(),
                    this._loadPrinters()
                ])
                await this._loadConfiguredPrinters() // needs workstation
            } finally {
                this.$_updateLoading(false)
            }
        }
    }

    private async _loadWorkstation(): Promise<void> {
        this.state.workstation = await anapathPrinterRestClientService.getWorkstation()
    }

    private async _loadPrinters(): Promise<void> {
        this.state.printers = await anapathPrinterRestClientService.getLocalPrinters()
    }

    private async _loadDevices(): Promise<void> {
        this.state.devices = await reportModelConfigurationService.getConfiguredPrinters(this.state.laboratoryId, this.state.workstation)
        this.state.devices = orderBy(this.state.devices, ['sortOrder'], ['asc'])
    }

    private async _loadReportModels(): Promise<void> {
        const reportModels = await reportModelConfigurationService.getConfiguredReportModels(this.state.laboratoryId)
        this.state.reportModels = reportModelConfigurationService.applyUserConfigurationToReportModels(reportModels, this.state.devices, this.state.printers)

        this.$forceUpdate()

        this.$store.commit('updatePrintDevices', this.state.reportModels)
    }

    private async _loadConfiguredPrinters(): Promise<void> {
        await this._loadDevices()
        await this._loadReportModels()
    }

    private static _mapReportModels(reportModels: ReportModelConfigurationMatch[]): UserappReportModelConfigurationRequest[] {
        return reportModels
            .filter(reportModel => reportModel.selectedPrinter)
            .map(reportModel => ({
                id: reportModel.userReportModelConfigurationId,
                reportModel: reportModel.reportModel,
                encodedPrinterName: reportModel.selectedPrinter?.code,
                copies: reportModel.copies,
                printerId: reportModel.printer?.id,
                path: reportModel.path
            }))
    }

    async savePrinters(): Promise<void> {
        const request = UserConfigurationPrintTab._mapReportModels(this.state.reportModels)

        const response = await this.$_toCallUnique(
            reportModelConfigurationService.saveDevicesConfiguration(this.state.laboratoryId, this.state.workstation, request),
            error => this.$_showErrorModalFromErrorResponse(error, this.$tc('userConfiguration.errorConf')))
        if (response) {
            this.$store.commit('updatePrintDevices', request)
            this.$_showToastSuccess()

            await this._loadConfiguredPrinters()
        }
    }
}
